import { BASE_URL } from '../types/constants';
import {
    AdvertisingConversionSummary,
    AnalyticsAudience,
    AnalyticsEventRequest,
    AnalyticsEventTypeCount,
    AnalyticsLabelCount,
    AnalyticsOverview,
    AnalyticsPlaybackPerformanceReport,
    AnalyticsRecentActivity,
    AnalyticsRetentionSummary,
    SubscriptionFunnelAnalytics,
    AnalyticsUserGrowth,
    AnalyticsUserWatchHistoryResponse,
    AnalyticsUserWatchTimelineResponse
} from '../types/analytics.types';
import { RestAPI } from './rest-api.service';
import { useUserState } from '../providers/user-state.provider.ts';
import { useCallback } from 'react';
import { sanitizeAnalyticsUrl } from '../utils/analytics-url';

const api = new RestAPI();
const analyticsSessionKey = 'analytics_session_id';
const analyticsAnonymousKey = 'analytics_anonymous_id';
const analyticsAttributionKey = 'analytics_attribution';
const marketingConsentStorageKey = 'combattv_marketing_consent';
const marketingConsentGranted = 'granted';
const metadataValueMaxLength = 200;
const metadataKeyMaxLength = 64;
const blockedMetadataKeys = new Set(['email', 'password', 'token', 'secret', 'authorization', 'firstname', 'lastname']);

interface AnalyticsAttributionState {
    landingPage?: string | null;
    utmSource?: string | null;
    utmMedium?: string | null;
    utmCampaign?: string | null;
    utmTerm?: string | null;
    utmContent?: string | null;
    gclid?: string | null;
    gbraid?: string | null;
    wbraid?: string | null;
    fbclid?: string | null;
    fbp?: string | null;
    fbc?: string | null;
}

export const getBrowserTimeZoneOffsetMinutes = () => new Date().getTimezoneOffset();

const asRecord = (value: unknown): Record<string, unknown> => {
    if (value && typeof value === 'object') {
        return value as Record<string, unknown>;
    }

    return {};
};

const readString = (record: Record<string, unknown>, camel: string, pascal: string): string => {
    const value = record[camel] ?? record[pascal];
    return typeof value === 'string' ? value : '';
};

const readNullableString = (record: Record<string, unknown>, camel: string, pascal: string): string | null => {
    const value = record[camel] ?? record[pascal];
    return typeof value === 'string' ? value : null;
};

const readCount = (record: Record<string, unknown>, camel: string, pascal: string): number => {
    const value = record[camel] ?? record[pascal];
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeDailyCounts = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            date: readString(record, 'date', 'Date'),
            count: readCount(record, 'count', 'Count')
        };
    });
};

const normalizeVideoCounts = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            videoId: readString(record, 'videoId', 'VideoId'),
            title: readString(record, 'title', 'Title'),
            count: readCount(record, 'count', 'Count')
        };
    });
};

const normalizeLabelCounts = (value: unknown): AnalyticsLabelCount[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            label: readString(record, 'label', 'Label'),
            count: readCount(record, 'count', 'Count'),
            uniqueUsers: readCount(record, 'uniqueUsers', 'UniqueUsers')
        };
    });
};

const normalizeEventTypeCounts = (value: unknown): AnalyticsEventTypeCount[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            eventType: readString(record, 'eventType', 'EventType'),
            count: readCount(record, 'count', 'Count'),
            uniqueUsers: readCount(record, 'uniqueUsers', 'UniqueUsers')
        };
    });
};

const normalizePlaybackPerformanceBuckets = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            label: readString(record, 'label', 'Label'),
            startupSampleCount: readCount(record, 'startupSampleCount', 'StartupSampleCount'),
            averageStartupMs: readCount(record, 'averageStartupMs', 'AverageStartupMs'),
            bufferEventCount: readCount(record, 'bufferEventCount', 'BufferEventCount'),
            averageBufferMs: readCount(record, 'averageBufferMs', 'AverageBufferMs'),
            seekSampleCount: readCount(record, 'seekSampleCount', 'SeekSampleCount'),
            averageSeekMs: readCount(record, 'averageSeekMs', 'AverageSeekMs'),
            playbackErrorCount: readCount(record, 'playbackErrorCount', 'PlaybackErrorCount')
        };
    });
};

const normalizeVideoPlaybackPerformanceRows = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        const fileSize = record.fileSizeBytes ?? record.FileSizeBytes;
        const parsedFileSize = typeof fileSize === 'number' ? fileSize : Number(fileSize);
        return {
            videoId: readString(record, 'videoId', 'VideoId'),
            videoShortId: readString(record, 'videoShortId', 'VideoShortId'),
            videoTitle: readString(record, 'videoTitle', 'VideoTitle'),
            videoStatus: readString(record, 'videoStatus', 'VideoStatus'),
            publishStatus: readString(record, 'publishStatus', 'PublishStatus'),
            quality: readString(record, 'quality', 'Quality'),
            transport: readString(record, 'transport', 'Transport'),
            bucketLabel: readString(record, 'bucketLabel', 'BucketLabel'),
            fileFormat: readString(record, 'fileFormat', 'FileFormat'),
            fileStatus: readNullableString(record, 'fileStatus', 'FileStatus'),
            filePath: readNullableString(record, 'filePath', 'FilePath'),
            fileSizeBytes: Number.isFinite(parsedFileSize) ? parsedFileSize : null,
            startupSampleCount: readCount(record, 'startupSampleCount', 'StartupSampleCount'),
            averageStartupMs: readCount(record, 'averageStartupMs', 'AverageStartupMs'),
            bufferEventCount: readCount(record, 'bufferEventCount', 'BufferEventCount'),
            averageBufferMs: readCount(record, 'averageBufferMs', 'AverageBufferMs'),
            seekSampleCount: readCount(record, 'seekSampleCount', 'SeekSampleCount'),
            averageSeekMs: readCount(record, 'averageSeekMs', 'AverageSeekMs'),
            playbackErrorCount: readCount(record, 'playbackErrorCount', 'PlaybackErrorCount'),
            lastPlayedAtUtc: readString(record, 'lastPlayedAtUtc', 'LastPlayedAtUtc')
        };
    });
};

const normalizePlaybackPerformance = (value: unknown) => {
    const record = asRecord(value);
    return {
        startupSampleCount: readCount(record, 'startupSampleCount', 'StartupSampleCount'),
        averageStartupMs: readCount(record, 'averageStartupMs', 'AverageStartupMs'),
        bufferEventCount: readCount(record, 'bufferEventCount', 'BufferEventCount'),
        averageBufferMs: readCount(record, 'averageBufferMs', 'AverageBufferMs'),
        seekSampleCount: readCount(record, 'seekSampleCount', 'SeekSampleCount'),
        averageSeekMs: readCount(record, 'averageSeekMs', 'AverageSeekMs'),
        playbackErrorCount: readCount(record, 'playbackErrorCount', 'PlaybackErrorCount'),
        buckets: normalizePlaybackPerformanceBuckets(record.buckets ?? record.Buckets),
        videos: normalizeVideoPlaybackPerformanceRows(record.videos ?? record.Videos)
    };
};

const normalizePlaybackPerformanceReport = (value: unknown): AnalyticsPlaybackPerformanceReport => {
    const record = asRecord(value);
    return {
        rangeStartUtc: readString(record, 'rangeStartUtc', 'RangeStartUtc'),
        rangeEndUtc: readString(record, 'rangeEndUtc', 'RangeEndUtc'),
        comparisonRangeStartUtc: readString(record, 'comparisonRangeStartUtc', 'ComparisonRangeStartUtc'),
        comparisonRangeEndUtc: readString(record, 'comparisonRangeEndUtc', 'ComparisonRangeEndUtc'),
        current: normalizePlaybackPerformance(record.current ?? record.Current),
        comparison: normalizePlaybackPerformance(record.comparison ?? record.Comparison)
    };
};

const normalizeCampaignCounts = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            source: readString(record, 'source', 'Source'),
            medium: (record.medium ?? record.Medium ?? null) as string | null,
            campaign: (record.campaign ?? record.Campaign ?? null) as string | null,
            count: readCount(record, 'count', 'Count'),
            uniqueUsers: readCount(record, 'uniqueUsers', 'UniqueUsers')
        };
    });
};

const normalizeRetention = (value: unknown): AnalyticsRetentionSummary => {
    const record = asRecord(value);
    return {
        activeUsers: readCount(record, 'activeUsers', 'ActiveUsers'),
        newUsers: readCount(record, 'newUsers', 'NewUsers'),
        returningUsers: readCount(record, 'returningUsers', 'ReturningUsers'),
        repeatUsers: readCount(record, 'repeatUsers', 'RepeatUsers'),
        averageActiveDays: readCount(record, 'averageActiveDays', 'AverageActiveDays'),
        dailyActiveUsers: normalizeDailyCounts(record.dailyActiveUsers ?? record.DailyActiveUsers)
    };
};

const normalizeRecentActivity = (value: unknown): AnalyticsRecentActivity[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            occurredAtUtc: readString(record, 'occurredAtUtc', 'OccurredAtUtc'),
            eventType: readString(record, 'eventType', 'EventType'),
            userName: (record.userName ?? record.UserName ?? null) as string | null,
            videoTitle: (record.videoTitle ?? record.VideoTitle ?? null) as string | null,
            path: (record.path ?? record.Path ?? null) as string | null,
            summary: (record.summary ?? record.Summary ?? null) as string | null
        };
    });
};

const normalizeAdvertisingConversions = (value: unknown): AdvertisingConversionSummary[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            provider: readString(record, 'provider', 'Provider'),
            eventName: readString(record, 'eventName', 'EventName'),
            status: readString(record, 'status', 'Status'),
            count: readCount(record, 'count', 'Count')
        };
    });
};

const normalizeLoginAudits = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        const successValue = record.isSuccess ?? record.IsSuccess;
        return {
            occurredAtUtc: readString(record, 'occurredAtUtc', 'OccurredAtUtc'),
            userName: (record.userName ?? record.UserName ?? null) as string | null,
            isSuccess: Boolean(successValue),
            ipAddress: (record.ipAddress ?? record.IpAddress ?? null) as string | null,
            userAgent: (record.userAgent ?? record.UserAgent ?? null) as string | null
        };
    });
};

const normalizeVideoAudits = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            occurredAtUtc: readString(record, 'occurredAtUtc', 'OccurredAtUtc'),
            videoId: readString(record, 'videoId', 'VideoId'),
            title: readString(record, 'title', 'Title'),
            userName: (record.userName ?? record.UserName ?? null) as string | null
        };
    });
};

const readOptionalString = (record: Record<string, unknown>, camel: string, pascal: string): string | null => {
    const value = record[camel] ?? record[pascal];
    return typeof value === 'string' && value.trim() ? value : null;
};

const readNullableNumber = (record: Record<string, unknown>, camel: string, pascal: string): number | null => {
    const value = record[camel] ?? record[pascal];
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const normalizeLocationBuckets = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            countryCode: readOptionalString(record, 'countryCode', 'CountryCode'),
            countryName: readString(record, 'countryName', 'CountryName') || 'Unknown',
            regionName: readOptionalString(record, 'regionName', 'RegionName'),
            city: readOptionalString(record, 'city', 'City'),
            latitude: readNullableNumber(record, 'latitude', 'Latitude'),
            longitude: readNullableNumber(record, 'longitude', 'Longitude'),
            count: readCount(record, 'count', 'Count'),
            uniqueUsers: readCount(record, 'uniqueUsers', 'UniqueUsers')
        };
    });
};

const normalizeDeviceBuckets = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            deviceType: readString(record, 'deviceType', 'DeviceType') || 'Unknown',
            operatingSystem: readString(record, 'operatingSystem', 'OperatingSystem') || 'Unknown',
            operatingSystemVersion: readOptionalString(record, 'operatingSystemVersion', 'OperatingSystemVersion'),
            browser: readString(record, 'browser', 'Browser') || 'Unknown',
            browserVersion: readOptionalString(record, 'browserVersion', 'BrowserVersion'),
            count: readCount(record, 'count', 'Count'),
            uniqueUsers: readCount(record, 'uniqueUsers', 'UniqueUsers')
        };
    });
};

const normalizeWatchHistoryItems = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        const progressValue = record.progressPercent ?? record.ProgressPercent;
        const progressParsed = typeof progressValue === 'number' ? progressValue : Number(progressValue);
        return {
            videoId: readString(record, 'videoId', 'VideoId'),
            videoShortId: (record.videoShortId ?? record.VideoShortId ?? null) as string | null,
            videoTitle: readString(record, 'videoTitle', 'VideoTitle'),
            firstWatchedAtUtc: readString(record, 'firstWatchedAtUtc', 'FirstWatchedAtUtc'),
            lastWatchedAtUtc: readString(record, 'lastWatchedAtUtc', 'LastWatchedAtUtc'),
            watchStartCount: readCount(record, 'watchStartCount', 'WatchStartCount'),
            watchProgressCount: readCount(record, 'watchProgressCount', 'WatchProgressCount'),
            watchCompleteCount: readCount(record, 'watchCompleteCount', 'WatchCompleteCount'),
            lastPositionSeconds: (record.lastPositionSeconds ?? record.LastPositionSeconds ?? null) as number | null,
            maxPositionSeconds: (record.maxPositionSeconds ?? record.MaxPositionSeconds ?? null) as number | null,
            durationSeconds: (record.durationSeconds ?? record.DurationSeconds ?? null) as number | null,
            progressPercent: Number.isFinite(progressParsed) ? progressParsed : 0,
            isCompleted: Boolean(record.isCompleted ?? record.IsCompleted),
            status: readString(record, 'status', 'Status') as 'Started' | 'InProgress' | 'Completed'
        };
    });
};

const normalizeWatchTimelineEvents = (value: unknown) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((item) => {
        const record = asRecord(item);
        return {
            occurredAtUtc: readString(record, 'occurredAtUtc', 'OccurredAtUtc'),
            eventType: readString(record, 'eventType', 'EventType'),
            positionSeconds: (record.positionSeconds ?? record.PositionSeconds ?? null) as number | null,
            durationSeconds: (record.durationSeconds ?? record.DurationSeconds ?? null) as number | null,
            path: (record.path ?? record.Path ?? null) as string | null,
            sessionId: (record.sessionId ?? record.SessionId ?? null) as string | null
        };
    });
};

const normalizeOverview = (value: unknown): AnalyticsOverview => {
    const record = asRecord(value);
    return {
        rangeStartUtc: readString(record, 'rangeStartUtc', 'RangeStartUtc'),
        rangeEndUtc: readString(record, 'rangeEndUtc', 'RangeEndUtc'),
        pageViewsSignedIn: normalizeDailyCounts(record.pageViewsSignedIn ?? record.PageViewsSignedIn),
        pageViewsSignedOut: normalizeDailyCounts(record.pageViewsSignedOut ?? record.PageViewsSignedOut),
        videoWatchStarts: normalizeDailyCounts(record.videoWatchStarts ?? record.VideoWatchStarts),
        videoSaves: normalizeDailyCounts(record.videoSaves ?? record.VideoSaves),
        videoPurchases: normalizeDailyCounts(record.videoPurchases ?? record.VideoPurchases),
        signups: normalizeDailyCounts(record.signups ?? record.Signups),
        subscriptionStarts: normalizeDailyCounts(record.subscriptionStarts ?? record.SubscriptionStarts),
        subscriptionCancellations: normalizeDailyCounts(record.subscriptionCancellations ?? record.SubscriptionCancellations),
        searches: normalizeDailyCounts(record.searches ?? record.Searches),
        searchResultClicks: normalizeDailyCounts(record.searchResultClicks ?? record.SearchResultClicks),
        recommendationClicks: normalizeDailyCounts(record.recommendationClicks ?? record.RecommendationClicks),
        topWatchedVideos: normalizeVideoCounts(record.topWatchedVideos ?? record.TopWatchedVideos),
        topSavedVideos: normalizeVideoCounts(record.topSavedVideos ?? record.TopSavedVideos),
        topPurchasedVideos: normalizeVideoCounts(record.topPurchasedVideos ?? record.TopPurchasedVideos),
        recentLogins: normalizeLoginAudits(record.recentLogins ?? record.RecentLogins),
        recentVideoWatches: normalizeVideoAudits(record.recentVideoWatches ?? record.RecentVideoWatches),
        playbackPerformance: normalizePlaybackPerformance(record.playbackPerformance ?? record.PlaybackPerformance),
        eventTypeCounts: normalizeEventTypeCounts(record.eventTypeCounts ?? record.EventTypeCounts),
        topSearchTerms: normalizeLabelCounts(record.topSearchTerms ?? record.TopSearchTerms),
        topInterests: normalizeLabelCounts(record.topInterests ?? record.TopInterests),
        topCampaigns: normalizeCampaignCounts(record.topCampaigns ?? record.TopCampaigns),
        retention: normalizeRetention(record.retention ?? record.Retention),
        recentActivity: normalizeRecentActivity(record.recentActivity ?? record.RecentActivity),
        advertisingConversions: normalizeAdvertisingConversions(record.advertisingConversions ?? record.AdvertisingConversions)
    };
};

const normalizeAudience = (value: unknown): AnalyticsAudience => {
    const record = asRecord(value);
    return {
        rangeStartUtc: readString(record, 'rangeStartUtc', 'RangeStartUtc'),
        rangeEndUtc: readString(record, 'rangeEndUtc', 'RangeEndUtc'),
        totalLogins: readCount(record, 'totalLogins', 'TotalLogins'),
        successfulLogins: readCount(record, 'successfulLogins', 'SuccessfulLogins'),
        failedLogins: readCount(record, 'failedLogins', 'FailedLogins'),
        uniqueUsers: readCount(record, 'uniqueUsers', 'UniqueUsers'),
        unknownLocationCount: readCount(record, 'unknownLocationCount', 'UnknownLocationCount'),
        locations: normalizeLocationBuckets(record.locations ?? record.Locations),
        devices: normalizeDeviceBuckets(record.devices ?? record.Devices)
    };
};

const normalizeUserGrowth = (value: unknown): AnalyticsUserGrowth => {
    const record = asRecord(value);
    const totals = asRecord(record.totals ?? record.Totals);
    const monthlyGrowthValue = record.monthlyGrowth ?? record.MonthlyGrowth;
    const monthlyGrowth = Array.isArray(monthlyGrowthValue)
        ? monthlyGrowthValue.map((item) => {
            const itemRecord = asRecord(item);
            return {
                monthStart: readString(itemRecord, 'monthStart', 'MonthStart'),
                newUsers: readCount(itemRecord, 'newUsers', 'NewUsers'),
                newMembers: readCount(itemRecord, 'newMembers', 'NewMembers'),
                newSubscribed: readCount(itemRecord, 'newSubscribed', 'NewSubscribed'),
                newPendingUnsubscribed: readCount(itemRecord, 'newPendingUnsubscribed', 'NewPendingUnsubscribed'),
                newUnsubscribed: readCount(itemRecord, 'newUnsubscribed', 'NewUnsubscribed')
            };
        })
        : [];

    return {
        rangeStartUtc: readString(record, 'rangeStartUtc', 'RangeStartUtc'),
        rangeEndUtc: readString(record, 'rangeEndUtc', 'RangeEndUtc'),
        monthlyGrowth,
        totals: {
            totalUsers: readCount(totals, 'totalUsers', 'TotalUsers'),
            totalEnabledUsers: readCount(totals, 'totalEnabledUsers', 'TotalEnabledUsers'),
            totalMembers: readCount(totals, 'totalMembers', 'TotalMembers'),
            totalSubscribers: readCount(totals, 'totalSubscribers', 'TotalSubscribers'),
            totalPendingUnsubscribed: readCount(totals, 'totalPendingUnsubscribed', 'TotalPendingUnsubscribed'),
            totalUnsubscribed: readCount(totals, 'totalUnsubscribed', 'TotalUnsubscribed')
        }
    };
};

const normalizeFunnelCollection = <T>(
    value: unknown,
    normalize: (record: Record<string, unknown>) => T
): T[] => Array.isArray(value) ? value.map((item) => normalize(asRecord(item))) : [];

export const normalizeSubscriptionFunnel = (value: unknown): SubscriptionFunnelAnalytics => {
    const record = asRecord(value);
    const summary = asRecord(record.summary ?? record.Summary);

    return {
        rangeStartUtc: readString(record, 'rangeStartUtc', 'RangeStartUtc'),
        rangeEndUtc: readString(record, 'rangeEndUtc', 'RangeEndUtc'),
        generatedAtUtc: readString(record, 'generatedAtUtc', 'GeneratedAtUtc'),
        completionWindowHours: readCount(record, 'completionWindowHours', 'CompletionWindowHours'),
        summary: {
            checkoutViews: readCount(summary, 'checkoutViews', 'CheckoutViews'),
            eligibleCheckoutViews: readCount(summary, 'eligibleCheckoutViews', 'EligibleCheckoutViews'),
            accountsReady: readCount(summary, 'accountsReady', 'AccountsReady'),
            eligibleAccountsReady: readCount(summary, 'eligibleAccountsReady', 'EligibleAccountsReady'),
            checkoutsReachingPayment: readCount(
                summary,
                'checkoutsReachingPayment',
                'CheckoutsReachingPayment'
            ),
            completed: readCount(summary, 'completed', 'Completed'),
            completedWithin24Hours: readCount(summary, 'completedWithin24Hours', 'CompletedWithin24Hours'),
            knownFailures: readCount(summary, 'knownFailures', 'KnownFailures'),
            checkoutConversionRate: readCount(summary, 'checkoutConversionRate', 'CheckoutConversionRate'),
            accountConversionRate: readCount(summary, 'accountConversionRate', 'AccountConversionRate')
        },
        stages: normalizeFunnelCollection(record.stages ?? record.Stages, (item) => ({
            stage: readString(item, 'stage', 'Stage'),
            count: readCount(item, 'count', 'Count'),
            uniqueUsers: readCount(item, 'uniqueUsers', 'UniqueUsers'),
            conversionFromPrevious: readCount(item, 'conversionFromPrevious', 'ConversionFromPrevious')
        })),
        failureReasons: normalizeFunnelCollection(
            record.failureReasons ?? record.FailureReasons ?? record.failures ?? record.Failures,
            (item) => ({
                failureCategory: readString(item, 'failureCategory', 'FailureCategory'),
                count: readCount(item, 'count', 'Count'),
                percentage: readCount(item, 'percentage', 'Percentage')
            })
        )
    };
};

const normalizeUserWatchHistoryResponse = (value: unknown): AnalyticsUserWatchHistoryResponse => {
    const record = asRecord(value);
    return {
        userId: readString(record, 'userId', 'UserId'),
        userName: readString(record, 'userName', 'UserName'),
        email: (record.email ?? record.Email ?? null) as string | null,
        rangeStartUtc: readString(record, 'rangeStartUtc', 'RangeStartUtc'),
        rangeEndUtc: readString(record, 'rangeEndUtc', 'RangeEndUtc'),
        page: readCount(record, 'page', 'Page'),
        pageSize: readCount(record, 'pageSize', 'PageSize'),
        totalCount: readCount(record, 'totalCount', 'TotalCount'),
        totalVideos: readCount(record, 'totalVideos', 'TotalVideos'),
        completedVideos: readCount(record, 'completedVideos', 'CompletedVideos'),
        inProgressVideos: readCount(record, 'inProgressVideos', 'InProgressVideos'),
        startedOnlyVideos: readCount(record, 'startedOnlyVideos', 'StartedOnlyVideos'),
        averageProgressPercent: readCount(record, 'averageProgressPercent', 'AverageProgressPercent'),
        items: normalizeWatchHistoryItems(record.items ?? record.Items)
    };
};

const normalizeUserWatchTimelineResponse = (value: unknown): AnalyticsUserWatchTimelineResponse => {
    const record = asRecord(value);
    return {
        userId: readString(record, 'userId', 'UserId'),
        userName: readString(record, 'userName', 'UserName'),
        email: (record.email ?? record.Email ?? null) as string | null,
        videoId: readString(record, 'videoId', 'VideoId'),
        videoShortId: (record.videoShortId ?? record.VideoShortId ?? null) as string | null,
        videoTitle: readString(record, 'videoTitle', 'VideoTitle'),
        rangeStartUtc: readString(record, 'rangeStartUtc', 'RangeStartUtc'),
        rangeEndUtc: readString(record, 'rangeEndUtc', 'RangeEndUtc'),
        events: normalizeWatchTimelineEvents(record.events ?? record.Events)
    };
};

const buildHeaders = (token?: string | null) => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json'
    };

    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    return headers;
};

export const getAnalyticsSessionId = () => {
    if (typeof window === 'undefined') {
        return 'server';
    }

    const generated = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    try {
        const existing = window.sessionStorage.getItem(analyticsSessionKey);
        if (existing) {
            return existing;
        }

        window.sessionStorage.setItem(analyticsSessionKey, generated);
    } catch {
        return generated;
    }

    return generated;
};

export const getAnalyticsAnonymousId = () => {
    if (typeof window === 'undefined') {
        return 'server';
    }

    try {
        const existing = window.localStorage.getItem(analyticsAnonymousKey);
        if (existing) {
            return existing;
        }

        const generated = createClientId();
        window.localStorage.setItem(analyticsAnonymousKey, generated);
        return generated;
    } catch {
        return getAnalyticsSessionId();
    }
};

const createClientId = () => (
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

/**
 * Creates a browser event id that can be shared between first-party analytics and ad-platform pixels.
 */
export const createAnalyticsClientEventId = () => createClientId();

const truncate = (value: string, maxLength: number) => (
    value.length > maxLength ? value.slice(0, maxLength) : value
);

const readCookie = (name: string) => {
    if (typeof document === 'undefined') {
        return null;
    }

    const prefix = `${name}=`;
    const match = document.cookie
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) : null;
};

const safeParseAttribution = (value: string | null): AnalyticsAttributionState => {
    if (!value) {
        return {};
    }

    try {
        return asRecord(JSON.parse(value)) as AnalyticsAttributionState;
    } catch {
        return {};
    }
};

const readAttributionFromUrl = (): AnalyticsAttributionState => {
    if (typeof window === 'undefined') {
        return {};
    }

    const params = new URLSearchParams(window.location.search);
    const read = (key: string) => params.get(key)?.trim() || null;
    const fbclid = read('fbclid');
    const fbc = readCookie('_fbc') ?? (fbclid ? `fb.1.${Math.floor(Date.now() / 1000)}.${fbclid}` : null);

    return {
        landingPage: sanitizeAnalyticsUrl(`${window.location.pathname}${window.location.search}`) || null,
        utmSource: read('utm_source'),
        utmMedium: read('utm_medium'),
        utmCampaign: read('utm_campaign'),
        utmTerm: read('utm_term'),
        utmContent: read('utm_content'),
        gclid: read('gclid'),
        gbraid: read('gbraid'),
        wbraid: read('wbraid'),
        fbclid,
        fbp: readCookie('_fbp'),
        fbc
    };
};

const buildAttribution = (): AnalyticsAttributionState => {
    if (typeof window === 'undefined') {
        return {};
    }

    let stored: AnalyticsAttributionState = {};
    try {
        stored = safeParseAttribution(window.localStorage.getItem(analyticsAttributionKey));
    } catch {
        stored = {};
    }
    const current = readAttributionFromUrl();
    const hasNewAttribution = Boolean(
        current.utmSource ||
        current.utmMedium ||
        current.utmCampaign ||
        current.utmTerm ||
        current.utmContent ||
        current.gclid ||
        current.gbraid ||
        current.wbraid ||
        current.fbclid ||
        current.fbp ||
        current.fbc
    );
    const next = hasNewAttribution
        ? { ...stored, ...current, landingPage: stored.landingPage ?? current.landingPage }
        : { ...stored, fbp: current.fbp ?? stored.fbp, fbc: current.fbc ?? stored.fbc };

    if (!next.landingPage) {
        next.landingPage = current.landingPage;
    }
    next.landingPage = sanitizeAnalyticsUrl(next.landingPage) ?? null;

    try {
        window.localStorage.setItem(analyticsAttributionKey, JSON.stringify(next));
    } catch {
        // Attribution is useful but non-critical; private browsing can disable localStorage.
    }

    return next;
};

const normalizeMetadataValue = (value: string | number | boolean | null | undefined) => {
    if (value === null || value === undefined) {
        return null;
    }

    return truncate(String(value), metadataValueMaxLength);
};

const sanitizeMetadata = (metadata?: AnalyticsEventRequest['metadata']) => {
    if (!metadata) {
        return undefined;
    }

    return Object.entries(metadata)
        .filter(([key, value]) => (
            key.trim().length > 0 &&
            !blockedMetadataKeys.has(key.trim().toLowerCase()) &&
            value !== undefined
        ))
        .slice(0, 20)
        .reduce<Record<string, string | null>>((result, [key, value]) => {
            result[truncate(key.trim(), metadataKeyMaxLength)] = normalizeMetadataValue(value);
            return result;
        }, {});
};

const readGrantedMarketingConsent = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.localStorage.getItem(marketingConsentStorageKey) === marketingConsentGranted
            ? marketingConsentGranted
            : null;
    } catch {
        return null;
    }
};

const enrichAnalyticsPayload = (payload: AnalyticsEventRequest): AnalyticsEventRequest => {
    const attribution = buildAttribution();
    const metadata = sanitizeMetadata(payload.metadata);
    const marketingConsent = readGrantedMarketingConsent();
    const path = payload.path ?? (
        typeof window === 'undefined' ? undefined : `${window.location.pathname}${window.location.search}`
    );
    const referrer = payload.referrer !== undefined ? payload.referrer : (
        typeof document === 'undefined' ? null : document.referrer || null
    );
    const landingPage = payload.landingPage ?? attribution.landingPage ?? null;

    return {
        ...payload,
        clientEventId: payload.clientEventId ?? createClientId(),
        clientOccurredAtUtc: payload.clientOccurredAtUtc ?? new Date().toISOString(),
        anonymousId: payload.anonymousId ?? getAnalyticsAnonymousId(),
        sessionId: payload.sessionId ?? getAnalyticsSessionId(),
        path: sanitizeAnalyticsUrl(path) ?? undefined,
        referrer: sanitizeAnalyticsUrl(referrer) ?? null,
        landingPage: sanitizeAnalyticsUrl(landingPage) ?? null,
        utmSource: payload.utmSource ?? attribution.utmSource ?? null,
        utmMedium: payload.utmMedium ?? attribution.utmMedium ?? null,
        utmCampaign: payload.utmCampaign ?? attribution.utmCampaign ?? null,
        utmTerm: payload.utmTerm ?? attribution.utmTerm ?? null,
        utmContent: payload.utmContent ?? attribution.utmContent ?? null,
        gclid: payload.gclid ?? attribution.gclid ?? null,
        gbraid: payload.gbraid ?? attribution.gbraid ?? null,
        wbraid: payload.wbraid ?? attribution.wbraid ?? null,
        fbclid: payload.fbclid ?? attribution.fbclid ?? null,
        fbp: payload.fbp ?? attribution.fbp ?? null,
        fbc: payload.fbc ?? attribution.fbc ?? null,
        metadata: marketingConsent
            ? { ...metadata, marketingConsent }
            : metadata
    };
};

export const postAnalyticsEvent = async (payload: AnalyticsEventRequest, token?: string | null) => {
    const enrichedPayload = enrichAnalyticsPayload(payload);
    try {
        const response = await fetch(`${BASE_URL}/v1/activity/events`, {
            method: 'POST',
            headers: buildHeaders(token),
            // Browser lifecycle analytics can fire during tab close or navigation.
            keepalive: true,
            body: JSON.stringify(enrichedPayload)
        });

        if (!response.ok) {
            throw new Error(`Analytics request failed with status ${response.status}`);
        }
    } catch {
        // Ignore analytics failures to avoid impacting UX.
        // In development we keep a signal for diagnosing broken tracking flows.
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('Analytics event failed to send', enrichedPayload.eventType);
        }
    }
};

export const postWatchProgressEvent = async (payload: AnalyticsEventRequest, token?: string | null) => {
    if (!token) {
        return;
    }

    const enrichedPayload = enrichAnalyticsPayload(payload);
    try {
        const response = await fetch(`${BASE_URL}/v1/watch/progress`, {
            method: 'POST',
            headers: buildHeaders(token),
            body: JSON.stringify(enrichedPayload)
        });

        if (!response.ok) {
            throw new Error(`Watch progress request failed with status ${response.status}`);
        }
    } catch {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('Watch progress failed to send', enrichedPayload.eventType);
        }
    }
};

export const fetchAnalyticsOverview = async (
    days: number,
    top: number,
    token?: string | null,
    excludeAdministrators = true,
    // JavaScript semantics: localTime = utcTime - offsetMinutes.
    timeZoneOffsetMinutes = getBrowserTimeZoneOffsetMinutes()
): Promise<AnalyticsOverview> => {
    const safeOffsetMinutes = Number.isFinite(timeZoneOffsetMinutes)
        ? Math.trunc(timeZoneOffsetMinutes)
        : 0;

    const searchParams = new URLSearchParams({
        days: days.toString(),
        top: top.toString(),
        excludeAdministrators: String(excludeAdministrators),
        timeZoneOffsetMinutes: safeOffsetMinutes.toString()
    });

    const response = await api.get(`${BASE_URL}/v1/admin/analytics/overview?${searchParams.toString()}`, token);
    return normalizeOverview(response);
};

export const fetchAnalyticsPlaybackPerformanceReport = async (
    from: string,
    to: string,
    compareFrom: string,
    compareTo: string,
    token?: string | null,
    excludeAdministrators = true,
    // JavaScript semantics: localTime = utcTime - offsetMinutes.
    timeZoneOffsetMinutes = getBrowserTimeZoneOffsetMinutes()
): Promise<AnalyticsPlaybackPerformanceReport> => {
    const safeOffsetMinutes = Number.isFinite(timeZoneOffsetMinutes)
        ? Math.trunc(timeZoneOffsetMinutes)
        : 0;

    const searchParams = new URLSearchParams({
        from,
        to,
        compareFrom,
        compareTo,
        excludeAdministrators: String(excludeAdministrators),
        timeZoneOffsetMinutes: safeOffsetMinutes.toString()
    });

    const response = await api.get(`${BASE_URL}/v1/admin/analytics/playback-performance?${searchParams.toString()}`, token);
    return normalizePlaybackPerformanceReport(response);
};

export const fetchAnalyticsUserGrowth = async (
    months: number,
    token?: string | null,
    excludeAdministratorsOrTimeZoneOffset: boolean | number = true,
    // JavaScript semantics: localTime = utcTime - offsetMinutes.
    timeZoneOffsetMinutes = getBrowserTimeZoneOffsetMinutes()
): Promise<AnalyticsUserGrowth> => {
    const excludeAdministrators = typeof excludeAdministratorsOrTimeZoneOffset === 'boolean'
        ? excludeAdministratorsOrTimeZoneOffset
        : true;
    const requestedOffsetMinutes = typeof excludeAdministratorsOrTimeZoneOffset === 'number'
        ? excludeAdministratorsOrTimeZoneOffset
        : timeZoneOffsetMinutes;
    const safeOffsetMinutes = Number.isFinite(requestedOffsetMinutes)
        ? Math.trunc(requestedOffsetMinutes)
        : 0;

    const searchParams = new URLSearchParams({
        months: months.toString(),
        excludeAdministrators: String(excludeAdministrators),
        timeZoneOffsetMinutes: safeOffsetMinutes.toString()
    });

    const response = await api.get(`${BASE_URL}/v1/admin/analytics/users/growth?${searchParams.toString()}`, token);
    return normalizeUserGrowth(response);
};

export const fetchSubscriptionFunnelAnalytics = async (
    months: number,
    token?: string | null,
    excludeAdministrators = true,
    timeZoneOffsetMinutes = getBrowserTimeZoneOffsetMinutes()
): Promise<SubscriptionFunnelAnalytics> => {
    const safeOffsetMinutes = Number.isFinite(timeZoneOffsetMinutes)
        ? Math.trunc(timeZoneOffsetMinutes)
        : 0;
    const searchParams = new URLSearchParams({
        months: months.toString(),
        excludeAdministrators: String(excludeAdministrators),
        timeZoneOffsetMinutes: safeOffsetMinutes.toString()
    });
    const response = await api.get(
        `${BASE_URL}/v1/admin/analytics/users/subscription-funnel?${searchParams.toString()}`,
        token
    );
    return normalizeSubscriptionFunnel(response);
};

/**
 * Fetch admin login audience analytics for the supplied inclusive date range.
 * Browser version splitting is off by default so device totals stay consolidated.
 */
export const fetchAnalyticsAudience = async (
    from: string,
    to: string,
    token?: string | null,
    excludeAdministrators = true,
    includeBrowserVersion = false
): Promise<AnalyticsAudience> => {
    const searchParams = new URLSearchParams({
        from,
        to,
        excludeAdministrators: String(excludeAdministrators),
        includeBrowserVersion: String(includeBrowserVersion)
    });

    const response = await api.get(`${BASE_URL}/v1/admin/analytics/audience?${searchParams.toString()}`, token);
    return normalizeAudience(response);
};

export interface AnalyticsUserWatchHistoryQuery {
    days?: number;
    page?: number;
    pageSize?: number;
    status?: 'all' | 'started' | 'inprogress' | 'completed';
    search?: string;
    sort?: 'lastWatchedAt_desc' | 'progress_desc' | 'title_asc';
}

export const fetchAnalyticsUserWatchHistory = async (
    userId: string,
    query: AnalyticsUserWatchHistoryQuery,
    token?: string | null
): Promise<AnalyticsUserWatchHistoryResponse> => {
    const searchParams = new URLSearchParams();
    if (query.days !== undefined) {
        searchParams.set('days', query.days.toString());
    }

    if (query.page !== undefined) {
        searchParams.set('page', query.page.toString());
    }

    if (query.pageSize !== undefined) {
        searchParams.set('pageSize', query.pageSize.toString());
    }

    if (query.status && query.status !== 'all') {
        searchParams.set('status', query.status);
    }

    if (query.search?.trim()) {
        searchParams.set('search', query.search.trim());
    }

    if (query.sort) {
        searchParams.set('sort', query.sort);
    }

    const response = await api.get(
        `${BASE_URL}/v1/admin/analytics/users/${encodeURIComponent(userId)}/watch-history?${searchParams.toString()}`,
        token
    );
    return normalizeUserWatchHistoryResponse(response);
};

export const fetchAnalyticsUserWatchTimeline = async (
    userId: string,
    videoId: string,
    days: number,
    limit: number,
    token?: string | null
): Promise<AnalyticsUserWatchTimelineResponse> => {
    const searchParams = new URLSearchParams({
        days: days.toString(),
        limit: limit.toString()
    });

    const response = await api.get(
        `${BASE_URL}/v1/admin/analytics/users/${encodeURIComponent(userId)}/watch-history/${encodeURIComponent(videoId)}/events?${searchParams.toString()}`,
        token
    );
    return normalizeUserWatchTimelineResponse(response);
};

export const useAnalyticsService = () => {
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;

    const trackEvent = useCallback(
        async (payload: AnalyticsEventRequest) => postAnalyticsEvent(payload, token),
        [token]
    );
    const trackWatchProgress = useCallback(
        async (payload: AnalyticsEventRequest) => postWatchProgressEvent(payload, token),
        [token]
    );
    const fetchOverview = useCallback(
        async (days: number, top: number, excludeAdministrators = true) =>
            fetchAnalyticsOverview(days, top, token, excludeAdministrators),
        [token]
    );
    const fetchUserGrowth = useCallback(
        async (months: number, excludeAdministrators = true) =>
            fetchAnalyticsUserGrowth(months, token, excludeAdministrators),
        [token]
    );
    const fetchSubscriptionFunnel = useCallback(
        async (months: number, excludeAdministrators = true) =>
            fetchSubscriptionFunnelAnalytics(months, token, excludeAdministrators),
        [token]
    );
    const fetchAudience = useCallback(
        async (from: string, to: string, excludeAdministrators = true, includeBrowserVersion = false) =>
            fetchAnalyticsAudience(from, to, token, excludeAdministrators, includeBrowserVersion),
        [token]
    );
    const fetchPlaybackPerformanceReport = useCallback(
        async (from: string, to: string, compareFrom: string, compareTo: string, excludeAdministrators = true) =>
            fetchAnalyticsPlaybackPerformanceReport(from, to, compareFrom, compareTo, token, excludeAdministrators),
        [token]
    );
    const fetchUserWatchHistory = useCallback(
        async (userId: string, query: AnalyticsUserWatchHistoryQuery) =>
            fetchAnalyticsUserWatchHistory(userId, query, token),
        [token]
    );
    const fetchUserWatchTimeline = useCallback(
        async (userId: string, videoId: string, days: number, limit: number) =>
            fetchAnalyticsUserWatchTimeline(userId, videoId, days, limit, token),
        [token]
    );

    return {
        trackEvent,
        trackWatchProgress,
        fetchOverview,
        fetchUserGrowth,
        fetchSubscriptionFunnel,
        fetchAudience,
        fetchPlaybackPerformanceReport,
        fetchUserWatchHistory,
        fetchUserWatchTimeline,
        getSessionId: getAnalyticsSessionId
    };
};
